// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps, StyledTypography } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import IconArrowRightLink from "@synd/XDL/Icons/LinkArrowRight";
import { buttonTransparent } from "@synd/XDL/PreStyle/button";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import color from "@synd/XDL/Utilities/color";
import xScreen from "@synd/XDL/Utilities/xScreen";
import * as React from "react";
import { css } from "styled-components";

export const linkArrowStyle: LinkPresentationProps = {
    icon: {
        iconProps: {
            src: IconArrowRightLink,
            size: 14,
            color: "primary",
            css: css`
                &,
                & > svg {
                    height: 16px;
                }
                opacity: 0.66;
                margin-left: 8px;
            `,
        },
        position: "right",
    },
    typographyProps: {
        size: 14,
        ellipsis: true,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 16px;
        `,
    },
    css: css`
        justify-content: left;
        // prevent take full width
        align-self: center;
        ${xScreen("lte", "md")} {
            align-self: flex-start;
        }
        &:hover {
            & > ${TypographyStyle} {
                text-decoration: underline;
            }
            & > ${IconWrapper} {
                opacity: 0.8;
            }
        }
    `,
};

interface IconButtonProps {
    typographyProps?: TypographyProps;
}

export const iconButtonCommonStyles = (
    iconSrc: React.ComponentType | string,
    iconSize?: number,
    buttonProps?: IconButtonProps,
    iconColor?: string,
) => {
    return {
        icon: {
            src: iconSrc,
            position: "left",
        },
        typographyProps: {
            color: "common.ui4",
            size: 13,
            weight: "bold",
            transform: "capitalize",
            ...buttonProps?.typographyProps,
            css: css`
                letter-spacing: 0;
                line-height: 14px;
                &:hover {
                    color: ${color("common.ui3")};
                }
                ${buttonProps?.typographyProps?.css}
            `,
        },
        css: css`
            padding: 0;
            ${buttonTransparent}
            ${IconWrapper} {
                top: 0;
                margin-right: 8px;
                &,
                & svg {
                    width: ${iconSize || 13}px;
                    height: ${iconSize || 13}px;
                }
                &,
                &:hover,
                &:focus {
                    color: ${iconColor ? color(iconColor) : color("common.ui4", 0.33)};
                }
            }

            &:hover:not([disabled]),
            &:focus:not([disabled]),
            &:focus:hover {
                &,
                & ${IconWrapper}, ${TypographyStyle} {
                    color: ${color("primary", 0.5)};
                }
            }

            &:disabled {
                & ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("common.primary", 0.5)};
                    background-color: transparent;
                }
            }

            &,
            * {
                font-size: ${`${buttonProps?.typographyProps?.size}px` || "13px"};
                font-weight: ${buttonProps?.typographyProps?.weight || "600"};
            }
        `,
    } as ButtonPresentationProps;
};

export const iconLinkCommonStyles = (iconSrc: React.ComponentType | string) => {
    return {
        icon: {
            iconProps: {
                src: iconSrc,
                size: 13,
                css: css`
                    width: 12px;
                    margin-right: 6px;
                    opacity: 0.66;
                `,
            },
            position: "left",
        },
        typographyProps: {
            size: 12,
            weight: fontConfig.weight.bold,
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
            &:disabled {
                opacity: 0.44;
            }
            &:not(:disabled):hover {
                & ${StyledTypography} {
                    text-decoration: underline;
                }
            }
        `,
    } as LinkPresentationProps;
};
